import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Check } from 'lucide-react';

import { accountContactSend } from '@graphql/mutations/account';
import { validateForEmptyField } from '@validation';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import Spinner from '@components/Spinner';
import { cn } from '@components/lib/utils';

interface FormError {
  fromEmail: string | null;
  message: string | null;
}

interface ContactFormProps {
  onClose: () => void;
  infoMessage?: string;
}

const HARMFUL_CONTENT_ERROR = 'Message contains harmful content';

const ContactForm: React.FC<ContactFormProps> = ({ onClose, infoMessage }) => {
  const [fromEmail, setFromEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<FormError>({
    fromEmail: null,
    message: null,
  });
  const [done, setDone] = useState<boolean>(false);

  const [accountContactSendMutation, { loading }] =
    useMutation(accountContactSend);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'modal_open_contact_me',
    });
  }, []);

  const clearErrors = useCallback(
    () => setError({ fromEmail: null, message: null }),
    [],
  );

  const onSubmit = useCallback(async () => {
    const emailError = validateForEmptyField(fromEmail);
    if (emailError) {
      setError((prev) => ({ ...prev, fromEmail: emailError }));
      return;
    }

    const messageError = validateForEmptyField(message);
    if (messageError) {
      setError((prev) => ({ ...prev, message: messageError }));
      return;
    }

    try {
      await accountContactSendMutation({
        variables: { input: { fromEmail, message } },
      });

      setDone(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      if (errorMessage === HARMFUL_CONTENT_ERROR) {
        setError({ fromEmail: null, message: errorMessage });
      } else {
        setError({ fromEmail: errorMessage, message: null });
      }
    }
  }, [fromEmail, message, accountContactSendMutation]);

  if (done) {
    return (
      <div className="flex h-[190px] w-full animate-in flex-col items-center justify-center duration-200 fade-in">
        <div className="mx-auto mb-5 flex size-[100px] items-center justify-center rounded-full bg-marble-green-50 text-marble-green-600 shadow-inner">
          <Check className="size-10" />
        </div>
        <h2 className="text-lg font-bold text-foreground">Message Sent!</h2>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-2.5 text-lg font-bold text-foreground">Contact me</h2>
      {infoMessage && (
        <p className="mb-[30px] text-sm text-muted-foreground">{infoMessage}</p>
      )}

      <label
        htmlFor="contact-email"
        className="mb-1.5 block text-sm text-foreground"
      >
        E-mail
      </label>
      <Input
        id="contact-email"
        type="email"
        name="email"
        placeholder="Enter your e-mail"
        value={fromEmail}
        aria-invalid={!!error.fromEmail}
        onChange={(field: React.ChangeEvent<HTMLInputElement>) => {
          setFromEmail(field.target.value);
          clearErrors();
        }}
      />
      {error.fromEmail && (
        <p className="mt-1 text-xs text-destructive">{error.fromEmail}</p>
      )}

      <label
        htmlFor="contact-message"
        className="mt-5 mb-1.5 block text-sm text-foreground"
      >
        Your message
      </label>
      <Textarea
        id="contact-message"
        name="message"
        placeholder="Enter your message"
        value={message}
        aria-invalid={!!error.message}
        onChange={(field: React.ChangeEvent<HTMLTextAreaElement>) => {
          setMessage(field.target.value);
          clearErrors();
        }}
      />
      {error.message && (
        <p className="mt-1 text-xs text-destructive">{error.message}</p>
      )}

      <div className="mt-[30px] flex justify-end gap-2.5">
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className={cn('cursor-pointer', 'disabled:cursor-not-allowed')}
          disabled={loading}
          onClick={onSubmit}
        >
          {loading ? <Spinner /> : 'Send'}
        </Button>
      </div>
    </div>
  );
};

export default ContactForm;
